import React, { useState, useEffect } from 'react';
import Typography from 'apollo-react/components/Typography';
import LinearProgress from 'apollo-react/components/LinearProgress';
import Box from 'apollo-react/components/Box';

/**
 * Global upload progress state management
 * Simple pub/sub pattern for tracking upload progress across components
 */
const progressStates = new Map();
const subscribers = new Set();

const notifySubscribers = () => {
  subscribers.forEach(callback => callback());
};

/**
 * Update upload progress for a specific field
 * @param {string} fieldId - Unique identifier for the upload field
 * @param {object} state - Upload state { uploading: boolean, progress: number }
 */
export const updateUploadProgress = (fieldId, state) => {
  progressStates.set(fieldId, state);
  notifySubscribers();
};

/**
 * Get current upload progress for a field
 * @param {string} fieldId - Field identifier
 * @returns {object} Current progress state
 */
const getUploadProgress = (fieldId) => {
  return progressStates.get(fieldId) || { uploading: false, progress: 0 };
};

/**
 * Upload Progress Indicator Component
 * Shows upload progress with a clean, professional design
 * Only renders when actively uploading
 */
const UploadProgress = ({ fieldName }) => {
  const [progressState, setProgressState] = useState(() => getUploadProgress(fieldName));
  
  useEffect(() => {
    const handleUpdate = () => {
      const newState = getUploadProgress(fieldName);
      setProgressState(newState);
    };
    
    subscribers.add(handleUpdate);
    
    return () => {
      subscribers.delete(handleUpdate);
    };
  }, [fieldName]);
  
  // Hide when not uploading
  if (!progressState.uploading) {
    return null;
  }
  
  return (
    <Box sx={{ 
      marginTop: 2,
      marginBottom: 1,
      padding: 2,
      backgroundColor: '#f8f9fa',
      borderRadius: 1,
      border: '1px solid #e9ecef'
    }}>
      <Typography 
        variant="body2" 
        sx={{ 
          marginBottom: 1, 
          color: '#495057',
          fontWeight: 500,
          fontSize: '0.875rem'
        }}
      >
        Uploading... {progressState.progress || 0}%
      </Typography>
      
      <LinearProgress 
        variant="determinate" 
        value={progressState.progress || 0} 
        sx={{ 
          height: 6,
          borderRadius: 1,
          backgroundColor: '#e9ecef',
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#007bff',
            borderRadius: 1,
          }
        }}
      />
    </Box>
  );
};

export default UploadProgress;
